import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gamepad2, Loader2, Pencil, Plus, Search, Wand2 } from "lucide-react";
import {
  createGameRuleGenerationSession,
  generationSessionStorageKey
} from "../services/gameRuleGenerationService";
import { listGameRuleTemplates } from "../services/gameRuleTemplateService";
import type { GameRuleTemplateListRow } from "../types/gameRuleTemplate";

export const PrizeLogicLibraryPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<GameRuleTemplateListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [generatingTemplateId, setGeneratingTemplateId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listGameRuleTemplates()
      .then(setRows)
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load templates.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows =
    search.trim().length === 0
      ? rows
      : rows.filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()));

  const startGenerate = (templateId: string) => {
    setGeneratingTemplateId(templateId);
    setError(null);
    void createGameRuleGenerationSession(templateId)
      .then((session) => {
        sessionStorage.setItem(
          generationSessionStorageKey(session.sessionId),
          JSON.stringify({
            draftStoredFileId: session.draftStoredFileId,
            fields: session.fields
          })
        );
        navigate(`/prizelogic/generate/${templateId}/${session.sessionId}`);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Could not start generation session.");
      })
      .finally(() => setGeneratingTemplateId(null));
  };

  const generationBusy = generatingTemplateId !== null;

  return (
    <div className="relative space-y-4">
      {generationBusy ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
            <p className="text-sm font-medium text-foreground">Starting generation…</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">Preparing your document in Word.</p>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">PrizeLogic Library</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Templates you can edit or use to generate new documents.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Link to="/prizelogic/listing" className="text-sm font-medium text-primary hover:underline">
            Generated documents
          </Link>
          <Link to="/prizelogic" className="text-sm font-medium text-primary hover:underline">
            Back
          </Link>
        </div>
      </div>
      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
          <button type="button" className="ml-2 underline" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}
      <button
        type="button"
        disabled={generationBusy}
        onClick={() => navigate("/prizelogic/create-template")}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-700 disabled:opacity-50"
      >
        <Plus className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        Create Game Rule Template
      </button>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          type="search"
          placeholder="Search PrizeLogic templates…"
          className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="rounded-lg border border-[var(--border)] bg-card divide-y divide-border overflow-hidden">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filteredRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No templates yet.</p>
        ) : (
          filteredRows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-3 py-3">
              <Gamepad2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 font-medium text-foreground text-sm truncate">{r.name}</span>
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  disabled={generationBusy}
                  className="rounded-md p-1.5 text-violet-600 transition-colors hover:bg-violet-100 dark:hover:bg-violet-950/40 disabled:opacity-50"
                  title="Edit template"
                  aria-label="Edit template"
                  onClick={() => navigate(`/prizelogic/templates/${r.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  disabled={generationBusy}
                  className="rounded-md p-1.5 text-violet-600 transition-colors hover:bg-violet-100 dark:hover:bg-violet-950/40 disabled:opacity-50"
                  title="Generate document from this template"
                  aria-label="Generate document from this template"
                  onClick={() => startGenerate(r.id)}
                >
                  <Wand2 className="h-4 w-4" strokeWidth={2} aria-hidden />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
